import { trigger } from "swr";
import { useState, useCallback } from "react";
import { notification } from "antd";
import catching from "src/utils/catching";
import request, { requestProps } from "../../ApiCall/request";
import { useHistory } from "react-router-dom";
import useAuth from "./useAuth";
import { useTranslation } from "react-i18next";
import useLocalization from "./useLocalization";

const useRequest = () => {
  const { getLanguage, getCurrency } = useLocalization();

  const [loading, setloading] = useState(false);
  const {
    push,
    goBack: back,
    location: { pathname },
  } = useHistory();
  const { t } = useTranslation();
  const loadingHandle = useCallback(
    (status: boolean) => {
      setloading(status);
    },
    [loading]
  );

  const { accessToken } = useAuth();
  const onCreate = async (
    url: string,
    data?: any,
    options: {
      notif?: boolean;
      successMessage?: string;
      errorMessage?: string;
      key?: string;
      redirectUrl?: string;
      goBack?: boolean;
    } = { notif: true, successMessage: t("Successfully registered") }
  ) => {
    loadingHandle(true);
    // return await request({ token: getToken })
    return await request({
      token: accessToken,
      headers: {
        language: getLanguage()?.language,
        currency: getCurrency()?.currencyId,
        clientPath: pathname,
      },
    })
      .post(url, data)
      .then(async (res) => {
        loadingHandle(false);
        if (options) {
          const {
            goBack,
            key,
            notif = true,
            successMessage = t("Successfully registered"),
            redirectUrl,
          } = options;
          key && (await trigger(`${key}`, true));
          notif &&
            notification.success({
              message: successMessage,
              placement: "topRight",
            });
          goBack && back();
          redirectUrl && push(redirectUrl);
        }
        return res;
      })
      .catch((err) => {
        if (options) {
          const { errorMessage } = options;
          errorMessage?.length &&
            notification.error({
              message: errorMessage,
              placement: "topRight",
            });
        }
        loadingHandle(false);
        catching(err);
        throw err;
      });
  };

  const onEdit = async (
    url: string,
    data?: any,
    options: {
      notif?: boolean;
      successMessage?: string;
      errorMessage?: string;
      key?: string;
      redirectUrl?: string;
      goBack?: boolean;
    } = { notif: true, successMessage: t("Edited successfully") }
  ) => {
    loadingHandle(true);
    return await request({
      token: accessToken,
      headers: {
        language: getLanguage()?.language,
        currency: getCurrency()?.currencyId,
        clientPath: pathname,
      },
    })
      .patch(url, data)
      .then(async (res) => {
        loadingHandle(false);
        if (options) {
          const {
            goBack,
            key,
            notif = true,
            successMessage = t("Edited successfully"),
            redirectUrl,
          } = options;
          key && (await trigger(`${key}`, true));

          notif &&
            notification.success({
              message: successMessage,
              placement: "topRight",
            });
          goBack && back();
          redirectUrl && push(redirectUrl);
        }
        return res;
      })
      .catch((err) => {
        loadingHandle(false);
        if (options) {
          const { errorMessage } = options;
          errorMessage?.length &&
            notification.error({
              message: errorMessage,
              placement: "topRight",
            });
        }
        catching(err);
        throw err;
      });
  };

  const onDelete = async (
    url: string,
    options: {
      notif?: boolean;
      successMessage?: string;
      errorMessage?: string;
      key?: string;
      redirectUrl?: string;
      goBack?: boolean;
    } = { notif: true, successMessage: t("Deleted successfully") }
  ) => {
    loadingHandle(true);
    return await request({
      token: accessToken,
      headers: {
        language: getLanguage()?.language,
        currency: getCurrency()?.currencyId,
        clientPath: pathname,
      },
    })
      .delete(url)
      .then(async (res) => {
        loadingHandle(false);
        if (options) {
          const { goBack, key, notif, successMessage, redirectUrl } = options;
          key && (await trigger(`${key}`, true));

          notif &&
            notification.success({
              message: successMessage,
              placement: "topRight",
            });
          goBack && back();
          redirectUrl && push(redirectUrl);
        }
        return res;
      })
      .catch((err) => {
        loadingHandle(false);
        if (options) {
          const { errorMessage } = options;
          errorMessage?.length &&
            notification.error({
              message: errorMessage,
              placement: "topRight",
            });
        }
        catching(err);
        throw err;
      });
  };

  const onGet = async (
    url: string,
    options?: {
      notif?: boolean;
      successMessage?: string;
      errorMessage?: string;
      key?: string;
      redirectUrl?: string;
      goBack?: boolean;
      accessToken?: string;
      requestProps?: requestProps;
      catcher?: boolean;
    }
  ) => {
    loadingHandle(true);
    console.log("loading1", loading);
    return await request({
      token: options?.accessToken || accessToken,
      ...options?.requestProps,
      headers: {
        language: getLanguage()?.language,
        currency: getCurrency()?.currencyId,
        clientPath: pathname,
        ...options?.requestProps?.headers,
      },
    })
      .get(url)
      .then(async (res) => {
        loadingHandle(false);
        if (options) {
          const { goBack, key, notif, successMessage, redirectUrl } = options;
          key && (await trigger(`${key}`, true));

          notif &&
            notification.success({
              message: successMessage,
              placement: "topRight",
            });
          goBack && back();
          redirectUrl && push(redirectUrl);
        }
        return res;
      })
      .catch((err) => {
        loadingHandle(false);
        if (options) {
          const { errorMessage, catcher } = options;
          errorMessage?.length &&
            notification.error({
              message: errorMessage,
              placement: "topRight",
            });
          catcher && catching(err);
        }
        // catching(err);
        throw err;
      });
  };

  const onDownload = async (
    url: string,
    options: {
      fileName: string;
      fileType?: string;
      notif?: boolean;
      successMessage?: string;
      errorMessage?: string;
      accessToken?: string;
      requestProps?: requestProps;
    }
  ) => {
    loadingHandle(true);

    return await request({
      token: options?.accessToken || accessToken,
      AxiosRequestConfig: { responseType: "blob" },
      ...options?.requestProps,
    })
      .get(url)
      .then(async (res) => {
        loadingHandle(false);
        const { notif, successMessage, fileName, fileType } = options;
        notif &&
          notification.success({
            message: successMessage,
            placement: "topRight",
          });
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          fileType ? `${fileName}.${fileType}` : fileName
        );
        document.body.appendChild(link);
        link.click();

        return res;
      })
      .catch((err) => {
        loadingHandle(false);
        if (options) {
          const { errorMessage } = options;
          errorMessage?.length &&
            notification.error({
              message: errorMessage,
              placement: "topRight",
            });
        }
        catching(err);
        throw err;
      });
  };

  return { onCreate, onEdit, onDelete, onGet, onDownload, loading };
};
export default useRequest;
